import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';

// JobPosting Schema for SEO
const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": "Trabalhe Conosco - NexuCapital",
    "description": "Junte-se à nossa equipe de especialistas em mineração e ativos minerários. Faça parte da nossa missão de maximizar valor com transparência e compliance.",
    "hiringOrganization": {
        "@type": "Organization",
        "name": "NexuCapital Empreendimentos LTDA",
        "sameAs": "https://nexucapital.com.br",
        "logo": {
            "@type": "ImageObject",
            "url": "https://nexucapital.com.br/assets/logos/logo-nexucapital-principal.png"
        }
    },
    "jobLocation": {
        "@type": "Place",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "R Maria Antonia Cota, 102",
            "addressLocality": "João Monlevade",
            "addressRegion": "MG",
            "postalCode": "35930-027",
            "addressCountry": "BR"
        }
    },
    "employmentType": ["FULL_TIME", "CONTRACTOR"],
    "datePosted": "2025-01-01",
    "validThrough": "2025-12-31"
};

// FAQ Schema generator
const generateFAQSchema = (items: any[]) => {
    if (!items || items.length === 0) return null;

    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    };
};

const CareersPage: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();

    const careers = t?.careers;
    const faqItems = t?.faq?.careers?.items || [];

    if (!careers) return null;

    // SEO Hook
    useSEO({
        title: careers.title || "Carreiras - NexuCapital",
        description: careers.heroSubtitle || "Junte-se à equipe NexuCapital",
        path: "/careers",
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Carreiras', url: '/careers' }
            ]),
            generateWebPageSchema({
                name: careers.title || "Carreiras - NexuCapital",
                description: careers.heroSubtitle || "Junte-se à equipe NexuCapital",
                url: "/careers",
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            }),
            jobPostingSchema,
            generateFAQSchema(faqItems)
        ].filter(Boolean)
    });

    const handleContactClick = () => {
        const subject = encodeURIComponent(careers.emailSubject || 'Candidatura - NexuCapital');
        const body = encodeURIComponent((careers.emailBody || '').replace(/\\n/g, '\n'));
        window.location.href = `mailto:rh@nexucapital.com.br?subject=${subject}&body=${body}`;
    };

    return (
        <PublicLayout>
            <main className="flex-grow">
                {/* Hero Section */}
                <div className="bg-primary text-white py-12 md:py-20 bg-gradient-to-br from-dark to-primary-dark relative overflow-hidden">
                    {/* Pattern overlay */}
                    <div
                        className="absolute inset-0 opacity-30 pointer-events-none"
                        style={{
                            backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23ffffff" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="%23ffffff" opacity="0.05"/><circle cx="50" cy="10" r="0.5" fill="%23ffffff" opacity="0.08"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>')`,
                            backgroundRepeat: 'repeat'
                        }}
                    />
                    <div className="container mx-auto px-4 text-center relative z-10">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-3 md:mb-4 animate-fade-in-up text-transparent bg-clip-text bg-gradient-to-br from-white to-secondary">
                            {careers.title}
                        </h1>
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto text-gray-200 animate-slide-up-fade leading-relaxed">
                            {careers.heroSubtitle}
                        </p>
                    </div>
                </div>

                {/* Mission Section */}
                <section className="py-10 md:py-16 lg:py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 lg:gap-12 max-w-4xl mx-auto">
                            {/* Image */}
                            <div className="w-full md:w-1/2">
                                <div className="rounded-lg overflow-hidden shadow-xl">
                                    <img
                                        src="/assets/imagens/f411f75e-5a26-40b9-84f7-49ddae1e9164.jpg"
                                        alt="Profissionais trabalhando em mineração"
                                        className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover"
                                    />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="w-full md:w-1/2 text-center md:text-left space-y-4 md:space-y-6">
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-primary">
                                    {careers.missionTitle}
                                </h2>
                                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                                    {careers.missionText1}
                                </p>
                                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                                    {careers.missionText2}
                                </p>
                                <div className="pt-2">
                                    <button
                                        onClick={handleContactClick}
                                        className="bg-primary hover:bg-primary-dark text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg w-full sm:w-auto"
                                    >
                                        {careers.sendResume}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </PublicLayout>
    );
};

export default CareersPage;
